import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { apiRetry } from "../../../../utils";
import { Table, Dropdown, Confirm, Icon, Popup } from "semantic-ui-react";
import { Line } from "@ant-design/charts";
import { Pie, Column, DualAxes } from "@ant-design/plots";
import {
  Form,
  Permission,
  Productionform,
  Waterform,
} from "../../../../api";
import { useAuth } from "../../../../hooks";
import "./WaterKPIsDashBoard.scss";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { CollapsibleSection, LoadingSpinner } from "../../../Shared";
//import { Renderer as SVGRenderer } from '@antv/g-svg';
import WaterSummary from "./WaterSummary";
import TableGraf from "./TableGraf";
import KPIExportIconButton from "../KPIExportIconButton";
import { drawWaterCustomCharts } from "./pdfWaterCharts";
import { buildWaterComparisonData, buildWaterProductionIntensityData, buildWaterDomesticIntensityData, buildSharedYScale } from "./waterChartData";
import {
  waterKPIsCodes,
  waterFormulasIndicators,
  determineWaterUnits,
  waterCodes,
  convertUnitsFinal,
} from "../../../../utils/dataAndFormulas/waterForms.js";
import { formatWhole, formatMagnitudeAdaptive } from "../../../../utils/numberFormat";

/*import {
  convertUnits,
 // convertUnitsFinal,
  formatValue,
} from "../../../../utils/dataAndFormulas/convert";*/
//import convert from "convert-units";
import {
  convertUnits,
} from "../../../../utils/dataAndFormulas/convert";

const permissionController = new Permission();
const waterController = new Waterform();
const formController = new Form();
const productionController = new Productionform();

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function WaterKPIsDashBoard(props) {
  const { site, sharedProductionForms, sharedWorkersByPeriod, siteName = "" } = props;
  const {
    user: { role },
    accessToken,
  } = useAuth();

  const { language, translations } = useLanguage();

  const t = (key) => translations[key] || key; // Función para obtener la traducción

  const [permissionActive, setPermissionsActive] = useState([]);
  const [baseline, setbaseline] = useState("");
  const [valueBaseline, setValueBaseline] = useState({});
  // Evita que el cálculo principal corra mientras el fetch de baseline sigue
  // en vuelo (data y baseline se cargan en efectos separados por [site] y
  // pueden resolver en cualquier orden -- mismo fix aplicado en Energía/Residuos)
  const [baselineLoaded, setBaselineLoaded] = useState(false);
  const [indicatorsLineBase, setIndicatorsLineBase] = useState([]);
  const [loading, setLoading] = useState(true);
  // const isAdmin = (role? role.name : "") === "admin";

  const [data, setData] = useState([]);
  const [totales, setTotales] = useState({});
  const [conceptos, setConceptos] = useState([]);
  const [configFormData, setConfigFormData] = useState(null);
  const [missingBaselineYears, setMissingBaselineYears] = useState([]);

  const [workersTotals, setWorkersTotals] = useState([]);
  const [workersTotalsByPeriod, setWorkersTotalsByPeriod] = useState([]);

  const [loadingCount, setLoadingCount] = useState(0);
  const isLoading = loadingCount > 0;
  const [lastYears, setLastYears] = useState({}); // resultados calculados
  const [totalesByFilters, setTotalesByFilters] = useState({}); // resultados calculados
  const [totalesProductionsByFilters, setTotalesProductionsByFilters] =
    useState({}); // resultados calculados

  const startLoading = () => setLoadingCount((prev) => prev + 1);
  const stopLoading = () => setLoadingCount((prev) => Math.max(prev - 1, 0));

  //declaración estado de filtros
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });

  useEffect(() => {
    (async () => {
      startLoading();
      try {
        setPermissionsActive([]);
        if (role) {
          const response = await permissionController.getMyPermissions(accessToken, true);
          setPermissionsActive(response);
        }
      } catch (error) {
        console.error(error);
        setPermissionsActive([]);
      } finally {
        stopLoading();
      }
    })();
  }, []);

  useEffect(() => {
    if (!site) return;

    const fetchData = async () => {
      startLoading();
      setData([]);
      try {
        // --- 1. Obtener años de línea base y últimos 5 años ---
        const prueba = await apiRetry(() => waterController.getWaterFormsByLineBaseYears(accessToken, site));

        setIndicatorsLineBase(prueba.waterForms.indicators);
        const yearsLineaBase = prueba.waterForms.years;
        const currentYear = new Date().getFullYear();
        const yearsUltimos5 = Array.from({ length: 5 }, (_, i) =>
          String(currentYear - i),
        );
        const añosCombinados = [
          ...new Set([...yearsLineaBase, ...yearsUltimos5]),
        ];

        // --- 2. Obtener datos de waterForms ---
        const responseWater = await apiRetry(() => waterController.getWaterFormsByLastYears(accessToken, site, añosCombinados));
        setData(responseWater.waterForms);
        setLoading(false);
      } catch (error) {
        //console.error(error);
        setData([]);
      } finally {
        stopLoading();
      }
    };

    fetchData();
  }, [site]);

  // Efecto de baseline SEPARADO del de datos: así un fallo/404 al buscar la
  // línea base (típico de un sitio recién creado, que todavía no tiene
  // línea base configurada) no pisa los datos de Agua ya cargados arriba.
  useEffect(() => {
    if (!site) return;

    const fetchBaseline = async () => {
      startLoading();
      setbaseline("");
      setValueBaseline({}); // permite que el cálculo corra igual aunque no haya baseline
      setBaselineLoaded(false);
      try {
        const responseBaseline =
          await apiRetry(() =>
            formController.getLastBaselineByFormBySite(
              accessToken,
              site,
              "production",
            )
          );

        if (responseBaseline.code === 200) {
          setbaseline(responseBaseline.baseline);

          //Para saber el total de los años de baseline sin necesidad de andar consultando varias veces
          const resultado = await Promise.all(
            Object.entries(responseBaseline.baseline)
              .filter(([anio, valor]) => valor !== null) // Filtra por los valores no nulos
              .map(async ([anio, valor]) => {
                let a = await getValueBaseLine(anio, valor); // Llamada a la función asincrónica
                return { [anio]: a }; // Devuelve el año y el valor como un objeto
              }),
          );

          // Combina todos los objetos en un único objeto final
          const resultadoFinal = resultado.reduce((acc, obj) => {
            return { ...acc, ...obj }; // Combina los objetos
          }, {});

          setValueBaseline(resultadoFinal);
        }
        // Si no hay baseline (404/400), valueBaseline queda como {} y el dashboard carga igual
      } catch (error) {
        //console.error(error);
        // Ante error, {} ya fue seteado: el dashboard igual carga con datos absolutos
      } finally {
        stopLoading();
        setBaselineLoaded(true);
      }
    };

    fetchBaseline();
  }, [site]);

  // Trabajadores compartidos desde DashBoard.js (misma fuente que Energía/
  // Combustibles/Efluentes) -- evita repetir el fetch acá adentro.
  useEffect(() => {
    const workersByPeriod = sharedWorkersByPeriod || {};
    setWorkersTotalsByPeriod(workersByPeriod);

    const workersAvgByYear = {};
    Object.entries(workersByPeriod).forEach(([year, monthsData]) => {
      const values = Object.values(monthsData || {});
      const validValues = values.filter((v) => v > 0);
      const avg =
        validValues.length > 0
          ? validValues.reduce((a, b) => a + b, 0) / validValues.length
          : 0;
      workersAvgByYear[year] = Math.round(avg);
    });
    setWorkersTotals(workersAvgByYear);
  }, [sharedWorkersByPeriod]);

  // Obtener configuración de campos activos del formulario
  useEffect(() => {
    (async () => {
      try {
        if (site) {
          const response = await formController.getFormsBySite(
            accessToken,
            site,
            "water",
            true,
          );
          // La respuesta es directamente un array de formularios
          if (Array.isArray(response) && response.length > 0) {
            setConfigFormData(response[0]);
          } else {
            setConfigFormData(null);
          }
        }
      } catch (error) {
        console.error("Error al obtener configFormData:", error);
        setConfigFormData(null);
      }
    })();
  }, [site]);

  // Función que obtiene los campos fuente de un KPI basándose en waterFormulasIndicators
  const getKPISourceFields = (kpiName) => {
    // Mapeo basado en la lógica real de waterFormulasIndicators
    const kpiSourceMap = {
      // KPIs con campo fuente directo
      water_domestic_municipal_network_kpis: [
        "water_domestic_municipal_network",
      ],
      municipal_network_water_production_kpis: [
        "municipal_network_water_production",
      ],
      domestic_groundwater_kpis: ["domestic_groundwater"],
      groundwater_production_kpis: ["groundwater_production"],
      rainwater_kpis: ["rainwater"],
      surface_water_kpis: ["surface_water"],
      recycled_water_kpis: ["recycled_water"],
      reused_water_kpis: ["reused_water"],
      steam_condensed_water_kpis: ["steam_condensed_water"],

      // KPIs calculados/agregados (no filtrar por campos individuales)
      total_water_production_kpis: null, // Agregación de múltiples campos de producción
      total_domestic_water_kpis: null, // Agregación de múltiples campos domésticos
      combined_total_water_kpis: null, // Agregación total
      total_production_water_per_unit_kpis: null, // Calculado
      total_domestic_water_per_employee_kpis: null, // Calculado
      total_combined_water_per_unit_kpis: null, // Calculado
      total_production_water_cost_per_unit_kpis: null, // Calculado
      total_domestic_water_cost_per_employee_kpis: null, // Calculado
      total_combined_water_cost_per_unit_kpis: null, // Calculado
      total_water_consumption_kpis: null, // Agregación total
    };

    return kpiSourceMap[kpiName];
  };

  // Determinar los campos únicos de indicadores y si tienen código
  // Filtra solo los campos que están activos en configFormData
  const determineFieldsKPIs = () => {
    const fields = new Set();

    Object.keys(waterKPIsCodes).forEach((key) => {
      const sourceFields = getKPISourceFields(key);

      // Si no tiene campos fuente (es un KPI calculado/agregado), siempre agregarlo
      if (sourceFields === null) {
        fields.add(key);
        return;
      }

      // Si hay configFormData, verificar si TODOS los campos fuente están activos
      if (configFormData?.fields && configFormData.fields.length > 0) {
        const allSourceFieldsActive = sourceFields.every((sourceFieldName) => {
          const fieldConfig = configFormData.fields.find(
            (f) => f.name === sourceFieldName,
          );
          return fieldConfig && fieldConfig.active !== false;
        });

        if (allSourceFieldsActive) {
          fields.add(key);
        }
      } else {
        // Si no hay configFormData, mostrar todos los campos (comportamiento por defecto)
        fields.add(key);
      }
    });

    return Array.from(fields);
  };

  // Determinar los campos únicos y si tienen código
  const determineFieldsCode = () => {
    const fields = new Set();

    Object.keys(waterCodes).forEach((key) => {
      fields.add(key);
    });

    return Array.from(fields);
  };

  useEffect(() => {
    (async () => {
      startLoading();
      try {
        if (data && baselineLoaded) {
          const previous_totals = await transformData1(data);

          calculateDataFinal(previous_totals);

          // Detectar años sin baseline para mostrar aviso
          const currentYear = new Date().getFullYear();
          const yearsToCheck = [0,1,2,3,4].map(i => String(currentYear - i));
          setMissingBaselineYears(yearsToCheck.filter(y => !valueBaseline?.[y]));

          // 🔹 Últimos 5 años dinámicos
          const lastFiveYears = [
            currentYear,
            currentYear - 1,
            currentYear - 2,
            currentYear - 3,
            currentYear - 4,
          ];

          // 🔹 Filtrar datos solo de esos 5 años
          const filteredByYears = data?.data
            ?.filter((item) => lastFiveYears.includes(Number(item.year))) // 👈 convierte el string
            ?.reduce((acc, item) => {
              const year = Number(item.year);
              if (!acc[year]) acc[year] = [];
              acc[year].push(item);
              return acc;
            }, {});

          // 🔹 Guardar los últimos cinco años en estado (si querés mostrar botones o algo)
          setLastYears(lastFiveYears);

          const results = await getLastThreeYearsData(filteredByYears);
          setTotalesByFilters(results);

          // Usar producción compartida desde DashBoard.js
          if (sharedProductionForms) {
            const totalesFinal = await calcularVPSVFSporMesPorAño(sharedProductionForms, baseline);
            setTotalesProductionsByFilters(totalesFinal);
          }
        }
      } catch (error) {
        //console.error(error);
      } finally {
        stopLoading();
      }
    })();
  }, [data, valueBaseline, baselineLoaded, configFormData, sharedProductionForms]);

  async function calcularVPSVFSporMesPorAño(documentosPorAño, baselines) {
    const resultado = {};

    for (const [year, docs] of Object.entries(documentosPorAño)) {
      const baselineType = baselines[year]; // "VPS" o "VFS"
      if (!baselineType) continue;

      const isProduccion = baselineType === "VPS";
      const prefix = isProduccion ? "VP" : "VF";

      resultado[year] = {};

      for (const doc of docs) {
        const mes = doc.period;
        if (!mes) continue;

        let totalMes = 0;

        // Recorremos todos los campos del documento
        for (const [key, campo] of Object.entries(doc)) {
          // buscamos solo los campos dinámicos de producción o facturación
          if (
            campo?.code &&
            campo.code.startsWith(prefix) &&
            !campo.code.startsWith(prefix + "S")
          ) {
            const value = Number(campo.value) || 0;
            const coef = Number(campo.standardized_coefficient) || 1;
            totalMes += value * coef;
          }
        }

        resultado[year][mes] = totalMes;
      }
    }

    return resultado;
  }

  //Calcula el valor de linea base por año hay que cambiar si es por vfs o vps
  const getValueBaseLine = async (year, type) => {
    try {
      if (type === "VPS") {
        const response1 =
          await productionController.getTotalBaselineByYearAndSite(
            accessToken,
            site,
            year,
            "standardized_production",
          );
        //setValueBaseline(response1.total)
        return response1.total;
      } else {
        const response1 =
          await productionController.getTotalBaselineByYearAndSite(
            accessToken,
            site,
            year,
            "standardized_billing",
          );
        //setValueBaseline(response1.total)
        return response1.total;
      }
    } catch (error) {
      //console.error(error);
      return 1;
    }
  };

  const transformData1 = async (data) => {
    // Inicializar el objeto de totales y los conceptos
    const totalsPerYear = {};
    const conceptsSet = [];
    const unitsCache = {}; // Aquí almacenaremos los datos de unidades por año

    // Unidades configuradas en configFormData tienen prioridad máxima
    const configUnits = {};
    if (configFormData?.fields && configFormData.fields.length > 0) {
      configFormData.fields.forEach((field) => {
        if (field.name && field.unit) {
          configUnits[field.name] = field.unit;
        }
      });
    }

    const fields = determineFieldsCode();

    // Verificar si data y data.data son válidos y contienen datos
    if (!data || !Array.isArray(data.data) || data.data.length === 0) {
      //console.log("No hay datos para procesar o data.data no es un array válido");
      return {}; // Retornar un objeto vacío o manejar el caso según se requiera
    }

    // Crear un array de años únicos de los datos de consumo
    const uniqueYears = [
      ...new Set(data.data.map((item) => String(item.year))),
    ];

    // Primero, obtener las unidades para todos los años requeridos
    //console.log('Obteniendo unidades para los años:', uniqueYears);
    const promises = uniqueYears.map(async (año) => {
      try {
        // Realizar la llamada para obtener las unidades para el año
        const units = await waterController.getLastUnitFieldsByYear(
          accessToken,
          site,
          año,
          fields,
        );

        // Guardar en el caché si las unidades son válidas
        if (units && units.fields) {
          unitsCache[año] = units;
          //console.log(`Unidades guardadas en caché para el año ${año}:`, units);
        } else {
          // console.log(`No se obtuvieron unidades válidas para el año ${año}`);
        }
      } catch (error) {
        //console.log(`Error al obtener unidades para el año ${año}:`, error);
      }
    });

    // Esperar a que todas las consultas de unidades se resuelvan
    await Promise.all(promises);

    // Ahora procesamos los datos de consumo usando las unidades en caché
    if (data.data !== undefined) {
      // Crear un array de promesas para manejar las llamadas asíncronas
      const consumptionPromises = data.data.map(async (item) => {
        const year = String(item.year);

        // Verificar si ya tenemos las unidades para este year en el caché
        const units = unitsCache[year];
        if (!units) {
          return;
        }

        // Iterar sobre los conceptos que tenemos en el item
        fields.forEach((concept) => {
          // Si el concepto existe, procesamos
          if (item[concept]) {
            // Añadir el concepto a la lista de concepts únicos
            conceptsSet.push(concept);

            // Inicializar el total para el year si no existe
            if (!totalsPerYear[concept]) {
              totalsPerYear[concept] = {};
            }

            // Inicializar el total para el year si no existe
            if (!totalsPerYear[concept][year]) {
              totalsPerYear[concept][year] = {
                total: 0,
                unit: configUnits[concept] || units.fields[concept] || item[concept]?.unit || "",
              };
            }

            // Sumar el valor de consumo
            totalsPerYear[concept][year].total += item[concept].value
              ? convertUnitsFinal(
                  item[concept].value,
                  item[concept].unit,
                  totalsPerYear[concept][year].unit,
                  concept,
                )
              : 0;
          }
        });
      });
      // Esperar a que todas las promesas de consumo se resuelvan
      await Promise.all(consumptionPromises);
    }

    return totalsPerYear;
  };

  const calculateDataFinal = async (totales) => {
    // Inicializar el objeto de totales y los conceptos
    const totalesFinal = { ...totales };
    const conceptosSet = [];

    if (totales !== undefined) {
      for (let item in totales) {
        if (totales.hasOwnProperty(item)) {
          for (let year in totales[item]) {
            // const valueB = await getValueBaseLine(year, baseline[year]);
            const valueB = valueBaseline[year];
            if (totales[item].hasOwnProperty(year)) {
              const fields = determineFieldsKPIs();

              // Iterar sobre los conceptos que tenemos en el item
              fields.forEach((concepto) => {
                // Añadir el concepto a la lista de conceptos únicos
                // Inicializar el total para el año si no existe
                if (!totalesFinal[concepto]) {
                  totalesFinal[concepto] = {};
                  conceptosSet.push(concepto);
                }

                // Inicializar el total para el año si no existe
                if (!totalesFinal[concepto][year]) {
                  totalesFinal[concepto][year] = {};
                }

                totalesFinal[concepto][year] = waterFormulasIndicators(
                  concepto,
                  year,
                  totalesFinal,
                  valueB === 0 ? 1 : valueB,
                  workersTotals[year],
                  // 1 ///TODO----falta cantidad de empleados
                );
              });
            }
          }
        }
      }
    }

    setConceptos(removeNulls(totalesFinal, conceptosSet));
    setTotales(totalesFinal);
  };

  /*
  const removeNulls = (data, conceptos) => {
    // Iterar sobre las claves externas
    for (let key in data) {
      if (data.hasOwnProperty(key)) {
        // Verificar si todos los valores internos son 0
        const allZero = Object.values(data[key]).every(
          (value) =>
            value?.total === 0 ||
            value?.total === undefined ||
            isNaN(value?.total) ||
            value === null ||
            value === false
        );

        // Si todos los valores son 0, eliminar la clave externa
        if (allZero) {
          conceptos = conceptos.filter((item) => item !== key);
        }
      }
    }
    return conceptos;
  };

}*/
  // Función para obtener los valores de un año y mes específicos (period en texto)
  const getDataByYearAndMonth = async (data, year, month) => {
    const results = {};
    const fields = determineFieldsCode();
    //const fields = determineFieldsKPIs(); // ✅ función correcta

    // console.log("🔍 fields que se van a buscar:", fields);
    // console.log("🔍 data recibido:", data);
    const unitsCache = {};

    const configUnits = {};
    if (configFormData?.fields && configFormData.fields.length > 0) {
      configFormData.fields.forEach((field) => {
        if (field.name && field.unit) {
          configUnits[field.name] = field.unit;
        }
      });
    }

    if (!data || !data || Object.keys(data).length === 0) {
      return {};
    }

    // Diccionario de meses
    const monthMap = {
      January: "01",
      February: "02",
      March: "03",
      April: "04",
      May: "05",
      June: "06",
      July: "07",
      August: "08",
      September: "09",
      October: "10",
      November: "11",
      December: "12",
    };

    const yearStr = String(year);
    const monthStr =
      typeof month === "string"
        ? monthMap[month] || ""
        : String(month).padStart(2, "0");

    if (!monthStr) {
      //console.warn("Mes inválido o no reconocido:", month);
      return {};
    }

    const yearData = data[yearStr];
    if (!Array.isArray(yearData)) {
      //console.warn("No hay datos para el año:", yearStr);
      return {};
    }

    const filteredData = yearData.filter(
      (item) => monthMap[item.period] === monthStr,
    );

    if (filteredData.length === 0) {
      //console.warn(`No hay datos para ${month} ${yearStr}`);
      return {};
    }

    // Procesar resultados por campo
    filteredData.forEach((item) => {
      const units = unitsCache[yearStr];

      fields.forEach((concept) => {
        if (item[concept]) {
          if (!results[concept]) {
            results[concept] = {
              total: 0,
              unit: configUnits[concept] || units?.fields[concept] || item[concept]?.unit || "",
            };
          }

          results[concept].total += item[concept].value
            ? convertUnitsFinal(
                item[concept].value,
                item[concept].unit,
                results[concept].unit,
                concept,
              )
            : 0;
        }
      });
    });

    // ==========================
    // 🔹 AGREGAR: Calcular producción total
    // ==========================

    // Suponiendo que tienes campos de producción en tu data
    // Ajusta según tu estructura real de datos
    // let totalProduction = 0;
    // let productionUnit = "";

    // // Si tienes producción en los datos mensuales
    // filteredData.forEach((item) => {
    //   if (item.productionUnit) {
    //     // 👈 ajusta el nombre del campo según tu estructura
    //     totalProduction += item.production.value || 0;
    //     productionUnit = item.production.unit || productionUnit;
    //   }
    // });

    // results.production = {
    //   total: totalProduction,
    //   unit: productionUnit || "L", // 👈 ajusta la unidad por defecto
    // };

    // Lista de conceptos de agua (sin costos)
    const waterConcepts = Object.keys(waterCodes).filter(
      (key) => !key.includes("cost"),
    );

    // Unidad estándar para sumar todos los conceptos de agua
    const standardUnit = "m3";
    let totalWaterMonth = 0;

    waterConcepts.forEach((concept) => {
      if (results[concept] && typeof results[concept].total === "number") {
        // Convertir cada valor a la unidad estándar antes de sumar
        const convertedValue = convertUnitsFinal(
          results[concept].total,
          results[concept].unit,
          standardUnit,
          concept,
        );
        totalWaterMonth += convertedValue;
      }
    });

    results.total_water_month = {
      total: totalWaterMonth,
      unit: standardUnit,
    };

    return results;
  };

  // ==========================
  // 🔹 Calcular ultimos 5 años
  // ==========================

  const getLastThreeYearsData = async (totalesByFilters) => {
    const currentYear = new Date().getFullYear();
    const lastFiveYears = [
      currentYear - 4,
      currentYear - 3,
      currentYear - 2,
      currentYear - 1,
      currentYear,
    ];

    const resultsByYear = {};

    for (const year of lastFiveYears) {
      resultsByYear[year] = {};

      for (const month of months) {
        const data = await getDataByYearAndMonth(totalesByFilters, year, month);
        resultsByYear[year][month] = data;
      }
    }
    return resultsByYear;
  };

  const removeNulls = (data, concepts) => {
    // Iterar sobre las claves externas
    for (let key in data) {
      if (data.hasOwnProperty(key)) {
        // Verificar si todos los valores internos son 0, null, undefined o NaN
        const allZero = Object.values(data[key]).every((value) => {
          // Si el valor es un objeto con propiedad total
          if (value && typeof value === "object" && "total" in value) {
            const total = value.total;
            return (
              total === 0 ||
              total === null ||
              total === undefined ||
              isNaN(total)
            );
          }
          // Si el valor no es un objeto válido
          return true;
        });

        // Si todos los valores son 0, eliminar la clave externa
        if (allZero) {
          concepts = concepts.filter((item) => item !== key);
        }
      }
    }
    return concepts;
  };

  const summaryRef = useRef();
  const tableGrafRef = useRef();
  const comparisonChartRef = useRef();
  const intensityIndustrialRef = useRef();
  const intensityDomesticRef = useRef();

  const yearsWithData = useMemo(() => {
    if (!data?.years || !conceptos || conceptos.length === 0) return [];
    return [...data.years]
      .sort((a, b) => b - a)
      .slice(0, 5)
      .filter(year =>
        conceptos.some(item => (totales[item]?.[year]?.total || 0) !== 0)
      );
  }, [data, conceptos, totales]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!data || !data.years || data.years.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "5em 0", color: "#777" }}>
        <div style={{ fontSize: "2em", marginBottom: "1em", color: "#ccc" }}>
          <Icon name="exclamation triangle" size="huge" color="gray" />
        </div>
        <div style={{ fontSize: "1.5em", fontWeight: "bold", marginBottom: "1em" }}>
          {t("no_data_available") || "No hay datos disponibles"}
        </div>
      </div>
    );
  }

  if (!conceptos || conceptos.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "5em 0", color: "#777" }}>
        <div style={{ fontSize: "2em", marginBottom: "1em", color: "#ccc" }}>
          <Icon name="exclamation triangle" size="huge" color="gray" />
        </div>
        <div style={{ fontSize: "1.5em", fontWeight: "bold", marginBottom: "1em" }}>
          {t("no_data_available") || "No hay datos disponibles"}
        </div>
        {missingBaselineYears.length > 0 && (
          <div style={{
            background: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "6px",
            padding: "12px 20px",
            marginTop: "1.5em",
            display: "inline-flex",
            alignItems: "center",
            gap: "10px",
            fontSize: "0.95em",
            textAlign: "left",
          }}>
            <Icon name="warning sign" color="yellow" />
            <span>
              Configurá el volumen producido (VPS) o el volumen facturado (VFS) como línea base para verlos correctamente.{" "}
              <Link to="/admin/data/productionforms" style={{ color: "#856404", fontWeight: "bold" }}>
                Configurar línea base →
              </Link>
            </span>
          </div>
        )}
      </div>
    );
  }

  const exportYears = data?.years ? [...data.years].sort((a, b) => Number(b) - Number(a)).slice(0, 5) : [];

  // Umbral de magnitud para inferir Regla 1 (entero) en la tabla del PDF —
  // mismo criterio que resolveRowRule más abajo (tabla en pantalla).
  const PDF_WATER_WHOLE_NUMBER_THRESHOLD = 100;

  // AJUSTES DE MAGNITUDES NUMÉRICAS aplicado a la tabla del PDF (antes
  // usaba `toFixed(2)` fijo + coma decimal). Replica resolveRowRule /
  // formatByRowRule de la tabla en pantalla: costos -> Regla 1, el resto
  // según magnitud (Regla 1 si es grande, si no Regla 2+).
  const formatWaterCellForPdf = (value, item) => {
    if (typeof value !== "number" || isNaN(value) || value === 0) return "-";
    let rule;
    if (item.includes("cost")) {
      rule = "whole";
    } else {
      const rowValues = Object.values(totales[item] || {})
        .map((entry) => entry?.total)
        .filter((v) => typeof v === "number" && !isNaN(v) && v !== 0)
        .map(Math.abs);
      rule = rowValues.length > 0 && Math.max(...rowValues) >= PDF_WATER_WHOLE_NUMBER_THRESHOLD ? "whole" : "adaptive";
    }
    const formatted = rule === "whole" ? formatWhole(value) : formatMagnitudeAdaptive(value);
    // autoTable dibuja esta celda con doc.text() nativo (no SVG) — el
    // espacio fino (U+202F) no existe en WinAnsi y rompe el texto (mismo
    // problema ya resuelto en GEI/Social/Residuos/Efluentes).
    return formatted.replace(/ /g, " ");
  };

  return (
    <div className="list-water-forms">

        {/* Aviso cuando hay años sin baseline VPS/VFS */}
        {missingBaselineYears.length > 0 && (
          <div style={{
            background: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "6px",
            padding: "10px 16px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            fontSize: "0.92em",
          }}>
            <Icon name="warning sign" color="yellow" />
            <span>
              Algunos indicadores (KPIs) para los años <strong>{missingBaselineYears.join(", ")}</strong> mostrarán valor 0. Configurá el volumen producido (VPS) o el volumen facturado (VFS) como línea base para verlos correctamente.{" "}
              <Link to="/admin/data/productionforms" style={{ color: "#856404", fontWeight: "bold" }}>
                Configurar línea base →
              </Link>
            </span>
          </div>
        )}

        {/* Sección de Resumen */}
        <div className="water-summary-section" ref={summaryRef}>
          <WaterSummary
            filters={filters}
            site={site}
            valueBaseline={valueBaseline}
            baselineLoaded={baselineLoaded}
            waterController={Waterform}
            totales={totalesByFilters}
            workersTotals={workersTotals}
            selectorSlot={<YearMonthSelector onChange={setFilters} availableYears={yearsWithData} />}
            exportSlot={
              <KPIExportIconButton
                title={t("water") || "Agua"}
                conceptos={conceptos}
                years={exportYears}
                totales={totales}
                codes={waterKPIsCodes}
                t={t}
                codes={waterKPIsCodes}
                formatCellValue={formatWaterCellForPdf}
                chartRefs={[
                  { ref: summaryRef, label: t("summary") || "Resumen" },
                ]}
                renderCustomCharts={(doc, chartCtx) => drawWaterCustomCharts(doc, chartCtx, {
                  totales,
                  filters,
                  resultsByYear: totalesByFilters,
                  resultsProductions: totalesProductionsByFilters,
                  resultsWorkers: workersTotalsByPeriod,
                  t,
                })}
                siteName={siteName}
              />
            }
          />
        </div>

        {/* Sección de Gráficos */}
        <div className="water-tablegraf-section" ref={tableGrafRef}>
          <TableGraf filters={filters} totales={totales} t={t} />
        </div>

        {/* Sección de Comparación */}
        <CollapsibleSection
          title={t("water_total_consumption_comparison_3_years")}
          className="water-comparison-section"
          ref={comparisonChartRef}
        >
          <WaterComparisonChart resultsByYear={totalesByFilters} t={t} />
        </CollapsibleSection>

        {/* Sección de Intensidad Industrial */}
        <CollapsibleSection
          title={t("industrial_water_intensity")}
          className="water-intensity-section"
          ref={intensityIndustrialRef}
        >
          <WaterProductionIntensityChart
            resultsByYear={totalesByFilters}
            t={t}
            filters={filters}
            resultsProductions={totalesProductionsByFilters}
          />
        </CollapsibleSection>

        {/* Sección de Intensidad Doméstica */}
        <CollapsibleSection
          title={t("domestic_water_intensity")}
          className="water-intensity-section"
          ref={intensityDomesticRef}
        >
          <WaterDomesticIntensityChart
            resultsByYear={totalesByFilters}
            t={t}
            filters={filters}
            resultsWorkers={workersTotalsByPeriod}
          />
        </CollapsibleSection>

        {/* Sección de Tabla de Datos */}
        <TableYears
          filters={filters}
          data={data}
          accessToken={accessToken}
          t={t}
          conceptos={conceptos}
          totales={totales}
          language={language}
          indicatorsLineBase={indicatorsLineBase}
          valueBaseline={valueBaseline}
          defaultOpen={true}
        />

        {/* Sección de Valores Intermedios */}
        {/* <IntermediateValuesDisplay
          resultsProductions={totalesProductionsByFilters}
          resultsWorkers={workersTotalsByPeriod}
          t={t}
        /> */}
      </div>
    );
}
function TableYears(props) {
  const { data, conceptos, totales, t, language, indicatorsLineBase, filters, defaultOpen = true } =
    props;
  const { year, month } = filters || {};
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [years, setYears] = useState([]);
  const [listConceptos, setListConceptos] = useState([]);
  const [months, setMoths] = useState([]);

  //const [uniqueFields, setUniqueFields] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);

  // KPIagu1, KPIagu16, KPIagu5, KPIagu8, KPIagu10, KPIagu15 — mejor interpretación en fábrica.
  const FORCE_LITERS_FIELDS = [
    "water_domestic_municipal_network_kpis", // KPIagu1
    "total_domestic_water_per_employee_kpis", // KPIagu16
    "groundwater_production_kpis", // KPIagu5
    "surface_water_kpis", // KPIagu8
    "reused_water_kpis", // KPIagu10
    "total_production_water_per_unit_kpis", // KPIagu15
  ];

  // Función para manejar cambios de unidad (comentada para uso futuro)
  /*const handleUnitChange = (rowIndex, newValue) => {
    setSelectedUnits({ ...selectedUnits, [rowIndex]: newValue.value });
  };*/

  // useEffect(() => {
  //   console.log("📊 TableGraf filtrando con:", { year, month });
  // }, [year, month]);

  useEffect(() => {
    (async () => {
      try {
        setYears(data.years ? data.years.sort((a, b) => b - a).slice(0, 5) : []);
      } catch (error) {
        //console.error(error);
      }
    })();
  }, [data]);

  useEffect(() => {
    (async () => {
      try {
        // Obtener todos los campos únicos

        let valor = {};
        conceptos.map(
          (field) => (valor[field] = obtenerUnidadDeOtrosAnios(totales, field)),
          // (field) => (valor[field] = determineWaterUnits(field)[0].value)
        );

        // KPIagu1, KPIagu16 (L/Trabajador) y KPIagu5, KPIagu8, KPIagu10, KPIagu15
        // (L/Unidad) se muestran siempre en litros para mejor interpretación en fábrica.
        FORCE_LITERS_FIELDS.forEach((field) => {
          if (valor[field]) valor[field] = "L";
        });

        setSelectedUnits(valor);
      } catch (error) {
        // console.error(error);
        setSelectedUnits([]);
      }
    })();
  }, [totales]);

  useEffect(() => {
    (async () => {
      try {
        setListConceptos(conceptos);
      } catch (error) {
        //console.error(error);
      }
    })();
  }, [conceptos]);

  // Buscar el primer año que tenga unidad definida
  const obtenerUnidadDeOtrosAnios = (datos, concepto) => {
    if (!datos[concepto]) return "";
    const años = Object.keys(datos[concepto]);
    const añoConUnidad = años.find((año) => datos[concepto][año]?.unit);
    return añoConUnidad ? datos[concepto][añoConUnidad].unit : "";
  };

  const getIndicators = (item) => {
    let result = indicatorsLineBase.find((value) => value.name === item);
    if (result) {
      return result?.yearBaseline || 0;
    }
    return 0; // Sin cambio
  };

  // Umbral de magnitud para inferir Regla 1 (entero) en filas sin regla explícita
  const WHOLE_NUMBER_THRESHOLD = 100;

  // Determina la regla de la fila por magnitud: costos y valores grandes -> Regla 1
  // (entero, espacio fino de miles); el resto -> Regla 2/2+ adaptativa
  const resolveRowRule = (item, values) => {
    if (item.includes("cost")) return "whole";
    const nonZero = values
      .filter((v) => typeof v === "number" && !isNaN(v) && v !== 0)
      .map(Math.abs);
    if (nonZero.length === 0) return "adaptive";
    return Math.max(...nonZero) >= WHOLE_NUMBER_THRESHOLD ? "whole" : "adaptive";
  };

  // Formatea un valor según la regla resuelta para su fila
  const formatByRowRule = (rule, value) => {
    if (typeof value !== "number" || isNaN(value) || value === 0) return "-";
    return rule === "whole" ? formatWhole(value) : formatMagnitudeAdaptive(value);
  };

  // Renderiza una píldora de comparativa rojo/verde
  const renderPill = (rule, value, prevValue) => {
    const numValue = value != null ? Number(value) : null;
    const numPrev = prevValue != null ? Number(prevValue) : null;

    if (numValue == null || numValue === 0) {
      return <span className="gei-value-pill pill-empty" />;
    }

    let pillClass;
    if (numPrev == null || numPrev === 0) {
      pillClass = "pill-neutral";
    } else if (numValue > numPrev) {
      pillClass = "pill-up";
    } else if (numValue < numPrev) {
      pillClass = "pill-down";
    } else {
      pillClass = "pill-neutral";
    }

    return (
      <span className={`gei-value-pill ${pillClass}`}>
        {formatByRowRule(rule, numValue)}
      </span>
    );
  };
  return (
    <>
      <div className="kpi-table-section">
      <div className="section-content">
      <Table celled structured size="small" className="gei-kpi-table" unstackable>
        <Table.Header>
          <Table.Row
            onClick={() => setIsOpen(!isOpen)}
            style={{ cursor: "pointer" }}
          >
            <Table.HeaderCell textAlign="center">{t("code")}</Table.HeaderCell>
            <Table.HeaderCell textAlign="center">{t("indicators")}</Table.HeaderCell>
            {years.map((year, index) => (
              <Table.HeaderCell textAlign="center" key={index}>{year}</Table.HeaderCell>
            ))}
            <Table.HeaderCell textAlign="center">{t("LB")}</Table.HeaderCell>
            <Table.HeaderCell textAlign="center" style={{ width: "200px" }}>
              <span style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {t("units")}
                <Icon name={isOpen ? "chevron up" : "chevron down"} style={{ marginLeft: "auto" }} />
              </span>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        {isOpen && <Table.Body>
          {conceptos.map((item, index) => {
            // Recopilar todos los valores de la fila para determinar decimales consistentes
            const rowValues = years
              .map(y => {
                const raw = totales[item]?.[y]?.total || 0;
                return convertUnitsFinal(
                  raw,
                  totales[item]?.[y]?.unit,
                  selectedUnits[item],
                  selectedUnits,
                );
              })
              .filter(v => typeof v === "number" && !isNaN(v));
            const lbRaw = convertUnitsFinal(
              totales[item]?.[getIndicators(item)]?.total || 0,
              totales[item]?.[getIndicators(item)]?.unit,
              selectedUnits[item],
              selectedUnits,
            );
            if (typeof lbRaw === "number" && !isNaN(lbRaw)) rowValues.push(lbRaw);

            const rowRule = resolveRowRule(item, rowValues);

            return (
            <React.Fragment key={index}>
              <Table.Row>
                <Table.Cell data-label={t("code")}>{waterKPIsCodes[item]}</Table.Cell>
                <Table.Cell data-label={t("indicators")}>{t(item)}</Table.Cell>
                {years.map((year, i) => {
                  if (totales[item]) {
                    const rawValue = convertUnitsFinal(
                      totales[item][year]?.total || 0,
                      totales[item][year]?.unit,
                      selectedUnits[item],
                      selectedUnits,
                    );
                    const prevRawValue = convertUnitsFinal(
                      totales[item][years[i + 1]]?.total || 0,
                      totales[item][years[i + 1]]?.unit,
                      selectedUnits[item],
                      selectedUnits,
                    );
                    const pill = renderPill(rowRule, rawValue, prevRawValue);
                    return (
                      <Table.Cell key={i} textAlign="center" data-label={year}>
                        <Popup
                          content={`Valor real: ${parseFloat(Number(totales[item][year]?.total || 0).toFixed(6))} ${totales[item][year]?.unit || selectedUnits[item]}`}
                          trigger={<span style={{ cursor: "help" }}>{pill}</span>}
                          position="top center"
                          size="tiny"
                        />
                      </Table.Cell>
                    );
                  }
                  return <Table.Cell key={i} data-label={year}>{""}</Table.Cell>;
                })}
                <Table.Cell data-label={t("LB")}>
                  <Popup
                    content={`Valor real: ${parseFloat(Number(totales[item][getIndicators(item)]?.total || 0).toFixed(6))} ${totales[item][getIndicators(item)]?.unit || selectedUnits[item]}`}
                    trigger={
                      <span style={{ cursor: "help" }}>
                        {formatByRowRule(rowRule, lbRaw)}
                      </span>
                    }
                    position="top center"
                    size="tiny"
                  />
                </Table.Cell>
                <Table.Cell data-label={t("units")}>
                  {selectedUnits[item]
                    ? item.includes("per_employee") ||
                      item.includes("water_domestic_municipal_network_kpis") ||
                      item.includes("domestic_groundwater_kpis")
                      ? `${selectedUnits[item]} / ${t("employee")}`
                      : item.includes("per_unit") ||
                          item.includes(
                            "municipal_network_water_production_kpis",
                          ) ||
                          item.includes("groundwater_production_kpis") ||
                          item.includes("rainwater_kpis") ||
                          item.includes("surface_water_kpis") ||
                          item.includes("recycled_water_kpis") ||
                          item.includes("reused_water_kpis") ||
                          item.includes("steam_condensed_water_kpis")
                        ? `${selectedUnits[item]} / ${t("unit")}`
                        : selectedUnits[item]
                    : "-"}
                  {/* Dropdown para convertir unidades (comentado para uso futuro)
                  <Dropdown
                    onChange={(e, data) => {
                      handleUnitChange(item, data);
                    }}
                    options={determineWaterUnits(item).map((ds) => {
                      return {
                        key: ds.key,
                        text: ds.name,
                        value: ds.value,
                      };
                    })}
                    search
                    inline
                    style={{ width: "100%" }}
                    value={selectedUnits[item]}
                  />
                  */}
                </Table.Cell>
              </Table.Row>
            </React.Fragment>
            );
          })}
        </Table.Body>}
      </Table>
      </div>
      </div>
    </>
  );
}

export const YearMonthSelector = ({ onChange, availableYears = [] }) => {
  const currentYear = new Date().getFullYear();

  const years = availableYears.length > 0
    ? [...availableYears].sort((a, b) => b - a).map(y => ({ key: y, text: y, value: y }))
    : [
        { key: currentYear, text: currentYear, value: currentYear },
        { key: currentYear - 1, text: currentYear - 1, value: currentYear - 1 },
        { key: currentYear - 2, text: currentYear - 2, value: currentYear - 2 },
        { key: currentYear - 3, text: currentYear - 3, value: currentYear - 3 },
        { key: currentYear - 4, text: currentYear - 4, value: currentYear - 4 },
      ];

  const months = [
    { key: 1, text: "Enero", value: 1 },
    { key: 2, text: "Febrero", value: 2 },
    { key: 3, text: "Marzo", value: 3 },
    { key: 4, text: "Abril", value: 4 },
    { key: 5, text: "Mayo", value: 5 },
    { key: 6, text: "Junio", value: 6 },
    { key: 7, text: "Julio", value: 7 },
    { key: 8, text: "Agosto", value: 8 },
    { key: 9, text: "Septiembre", value: 9 },
    { key: 10, text: "Octubre", value: 10 },
    { key: 11, text: "Noviembre", value: 11 },
    { key: 12, text: "Diciembre", value: 12 },
  ];

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1-12

  useEffect(() => {
    if (availableYears && availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      const sorted = [...availableYears].sort((a, b) => b - a);
      const newYear = sorted[0];
      setSelectedYear(newYear);
      onChange({ year: newYear });
    }
  }, [availableYears]);

  const handleYearChange = (e, { value }) => {
    setSelectedYear(value);
    onChange({ year: value });
  };

  return (
    <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
      <Dropdown
        placeholder="Año"
        selection
        options={years}
        value={selectedYear}
        onChange={handleYearChange}
        className="year-filter-dropdown"
      />
    </div>
  );
};

///Grafico de comparacion consumo ultimos 3 años
function WaterComparisonChart({ resultsByYear, t }) {
  // Datos del gráfico — misma lógica que antes, extraída a
  // waterChartData.js para poder reusarla también desde el export a PDF
  // (pdfWaterCharts.js).
  const { years, barData, lineData, yearColorMap } = useMemo(
    () => buildWaterComparisonData(resultsByYear, t),
    [resultsByYear, t]
  );
  // Renderizador de la leyenda estilo "Pills"
const CustomLegend = () => (
  <div style={{
    display: "flex",
    justifyContent: "flex-start", // 👈 Alinea a la izquierda
    gap: "10px",
    marginBottom: "20px",
    paddingLeft: "5px", // Ajusta según el padding de tu contenedor de gráfico
    flexWrap: "wrap",
  }}>
    {years.map((year) => {
      const isActive = activeYears.includes(year);
      return (
        <div
          key={year}
          onClick={() => toggleYear(year)}
          style={{
            backgroundColor: isActive ? yearColorMap[year] : "#f3f4f6", 
            color: isActive ? "white" : "#9ca3af",
            padding: "4px 20px", // Un poco más compacto
            borderRadius: "20px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            border: isActive ? `1px solid ${yearColorMap[year]}` : "1px solid #d1d5db",
            boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
            userSelect: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth:"100px"
          }}
        >
          {year}
        </div>
      );
    })}
  </div>
);

const [activeYears, setActiveYears] = useState(years); 

// Función para alternar años
const toggleYear = (year) => {
  if (activeYears.includes(year)) {
    // Si solo queda uno, podrías elegir no dejar que se desactive el último
    setActiveYears(activeYears.filter(y => y !== year));
  } else {
    setActiveYears([...activeYears, year]);
  }
};

const filteredBarData = useMemo(() => 
  barData.filter(d => activeYears.includes(String(d.year))), 
[barData, activeYears]);

const filteredLineData = useMemo(() =>
  lineData.filter(d => activeYears.includes(String(d.year))),
[lineData, activeYears]);

  const sharedYScale = buildSharedYScale(filteredBarData, filteredLineData);

  const config = {
    xField: "month",
    legend:false,
   scale: {
    color: {
      domain: Object.keys(yearColorMap),
      range: Object.values(yearColorMap),
    },
  },
    tooltip: {
      items: [
        (item) => {
          // Cambiar el marcador para Promedio
          const marker = item.type?.includes("Promedio") ? "- -" : "";
          return {
            name: `${marker} ${item.type}`,
            value: formatWhole(item.value),
          };
        },
      ],
    },
    // legend: {
    //     position: "bottom"
    //   },
    children: [
      {
              data: filteredBarData, // <--- Datos filtrados
        type: "interval",
        yField: "value",
        colorField: "year",
        seriesField: "year", // 🔹 esto separa las barras por  año
        group: true,
        dodgePadding: 2,      //  evita que sobresalga
      intervalPadding: 4,   //  separa columnas y evita “salida” del eje
       scale: {
        color: ({ year }) => yearColorMap[year], // 👈 ACA
        y: sharedYScale,
    },
      },
      {
        data: filteredLineData,
        type: "line",
        yField: "value",
        colorField: "year",
         style: {lineWidth: 2,
        lineDash: [4, 8] },
         axis: { y: false },
         scale: { y: sharedYScale },
      // axis: { y: { position: "right" } },
      // scale: { series: { independent: true } },
      interaction: {
        tooltip: {
          crosshairs: false,
          marker: false,
        },
      },
      },
    ],
    axis: {
  x: {
    title: false,
    labelAutoHide: false,
    labelAutoRotate: false,
  },
  y: {
      title: false, // Un solo eje Y a la izquierda
      grid: true,
      labelFormatter: (v) => formatWhole(v),
    }}
  };
  return (
    <div style={{ height: 350, marginTop: 20, marginBottom: 20 }}>
      {/* 🟢 NUEVA LEYENDA ESTILO IMAGEN */}
    {barData.length > 0 && <CustomLegend />}
      {barData.length > 0 ? <DualAxes {...config} /> : <p>Cargando datos...</p>}
    </div>
  );
}

//Grafico de intensidad de produccion ultimos 3 años
function WaterProductionIntensityChart({
  resultsByYear,
  t,
  resultsProductions,
}) {
  // Datos del gráfico — misma lógica que antes, extraída a
  // waterChartData.js para poder reusarla también desde el export a PDF
  // (pdfWaterCharts.js).
  const { years, consumptionData, averageData, yearColorMap } = useMemo(
    () => buildWaterProductionIntensityData(resultsByYear, resultsProductions, t),
    [resultsByYear, resultsProductions, t]
  );
    // Renderizador de la leyenda estilo "Pills"
  const CustomLegend = () => (
    <div style={{
      display: "flex",
      justifyContent: "flex-start", // 👈 Alinea a la izquierda
      gap: "10px",
      marginBottom: "20px",
      paddingLeft: "5px", // Ajusta según el padding de tu contenedor de gráfico
      flexWrap: "wrap",
    }}>
      {years.map((year) => {
        const isActive = activeYears.includes(year);
        return (
          <div
            key={year}
            onClick={() => toggleYear(year)}
            style={{
              backgroundColor: isActive ? yearColorMap[year] : "#f3f4f6", 
              color: isActive ? "white" : "#9ca3af",
              padding: "4px 20px", // Un poco más compacto
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              border: isActive ? `1px solid ${yearColorMap[year]}` : "1px solid #d1d5db",
              boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth:"100px"
            }}
          >
            {year}
          </div>
        );
      })}
    </div>
  );
  
  // Dentro de tu componente FuelsComparisonChart
  const [activeYears, setActiveYears] = useState(years); 
  
  // Función para alternar años
  const toggleYear = (year) => {
    if (activeYears.includes(year)) {
      // Si solo queda uno, podrías elegir no dejar que se desactive el último
      setActiveYears(activeYears.filter(y => y !== year));
    } else {
      setActiveYears([...activeYears, year]);
    }
  };
  
  const filteredConsumptionData = useMemo(() => 
    consumptionData.filter(d => activeYears.includes(String(d.year))), 
  [consumptionData, activeYears]);
  
  const filteredAverageData = useMemo(() =>
    averageData.filter(d => activeYears.includes(String(d.year))),
  [averageData, activeYears]);

  const sharedYScale = buildSharedYScale(filteredConsumptionData, filteredAverageData);

  const config = {
    xField: "month",
      scale: {
    color: {
      domain: Object.keys(yearColorMap),
      range: Object.values(yearColorMap),
    },
  },
    tooltip: {
  items: [
    (item) => {
      // 1. Manejo para ítems con nombre (si existen)
      if (item.name) {
        return {
          name: item.name,
          value: item.count,
          color: yearColorMap[item.year],
        };
      }

      // 2. VALIDACIÓN CLAVE: Si el valor es null o undefined, no ejecutamos toFixed
      if (item.value === null || item.value === undefined) {
        return {
          name: item.type,
          value: "-", // O lo que prefieras mostrar cuando no hay datos
        };
      }

      // 3. Manejo para Promedio y Consumo con datos válidos
      const marker = item.type?.includes("Promedio") ? "- -" : "";
      return {
        name: `${marker} ${item.type}`,
        value: typeof item.value === 'number' ? formatMagnitudeAdaptive(item.value) : item.value,
      };
    },
  ],
},
    legend:false,
    axis: {
      y: { labelFormatter: (v) => formatWhole(v) },
    },
    children: [
      {
        data: filteredConsumptionData,
        type: "line",
        yField: "value",
        colorField: "year",
       // seriesField: "year",
        style: { lineWidth: 4 },
        scale: { y: sharedYScale },
               connectNulls: false,
      },
      {
        data: filteredAverageData,
        type: "line",
        yField: "value",
        colorField: "year",
                 axis: { y: false },
        scale: { y: sharedYScale },
        style: {lineWidth: 2,
        lineDash: [4, 8] },
           connectNulls: false,
      },
    ],
  };

  return (
    <div style={{ height: 450, marginTop: 20, marginBottom: 20 }}>
         {/* 🟢 NUEVA LEYENDA ESTILO IMAGEN */}
    {consumptionData.length > 0 && <CustomLegend />}
      {consumptionData.length ? (
             <DualAxes {...config} style={{ height: 400 }} />
           ) : (
             <div style={{
               display: "flex",
               alignItems: "center",
               justifyContent: "center",
               height: 400,
               color: "#999",
             }}>
               No hay datos disponibles
             </div>
           )}
    </div>
  );
}

//Grafico de intensidad domestica ultimos 3 años
function WaterDomesticIntensityChart({ resultsByYear, t, resultsWorkers }) {
  // Datos del gráfico — misma lógica que antes, extraída a
  // waterChartData.js para poder reusarla también desde el export a PDF
  // (pdfWaterCharts.js).
  const { years, consumptionData, averageData, yearColorMap } = useMemo(
    () => buildWaterDomesticIntensityData(resultsByYear, resultsWorkers, t),
    [resultsByYear, resultsWorkers, t]
  );

  
    // Renderizador de la leyenda estilo "Pills"
  const CustomLegend = () => (
    <div style={{
      display: "flex",
      justifyContent: "flex-start", // 👈 Alinea a la izquierda
      gap: "10px",
      marginBottom: "20px",
      paddingLeft: "5px", // Ajusta según el padding de tu contenedor de gráfico
      flexWrap: "wrap",
    }}>
      {years.map((year) => {
        const isActive = activeYears.includes(year);
        return (
          <div
            key={year}
            onClick={() => toggleYear(year)}
            style={{
              backgroundColor: isActive ? yearColorMap[year] : "#f3f4f6", 
              color: isActive ? "white" : "#9ca3af",
              padding: "4px 20px", // Un poco más compacto
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              border: isActive ? `1px solid ${yearColorMap[year]}` : "1px solid #d1d5db",
              boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth:"100px"
            }}
          >
            {year}
          </div>
        );
      })}
    </div>
  );
  
  // Dentro de tu componente FuelsComparisonChart
  const [activeYears, setActiveYears] = useState(years); 
  
  // Función para alternar años
  const toggleYear = (year) => {
    if (activeYears.includes(year)) {
      // Si solo queda uno, podrías elegir no dejar que se desactive el último
      setActiveYears(activeYears.filter(y => y !== year));
    } else {
      setActiveYears([...activeYears, year]);
    }
  };
  
  const filteredConsumptionData = useMemo(() => 
    consumptionData.filter(d => activeYears.includes(String(d.year))), 
  [consumptionData, activeYears]);
  
  const filteredAverageData = useMemo(() =>
    averageData.filter(d => activeYears.includes(String(d.year))),
  [averageData, activeYears]);

  const sharedYScale = buildSharedYScale(filteredConsumptionData, filteredAverageData);

  // 🔹 Config del gráfico
  const config = {
    xField: "month",
      // 🔹 Escala global de colores para ambas capas
  scale: {
    color: {
      domain: Object.keys(yearColorMap),
      range: Object.values(yearColorMap),
    },
  },
       tooltip: {
      items: [
        (item) => ({
        name: item.type,
        value: formatWhole(item.value),
        color: yearColorMap[item.year],
      }),
      ],
    },
    legend:false,
    axis: {
      y: { labelFormatter: (v) => formatWhole(v) },
    },
    children: [
      {
        data: filteredConsumptionData,
        type: "line",
        yField: "value",
        colorField: "year",
        group: true,
        style: { maxWidth: 80, lineWidth: 4 },
        scale: { y: sharedYScale },
     connectNulls: false,
      },
      {
        data: filteredAverageData,
        type: "line",
        yField: "value",
        colorField: "year",

          axis: { y: false },
      scale: { y: sharedYScale },
      style: {
        lineWidth: 2,
        lineDash: [4, 8],
      },
         connectNulls: false,
      },
    ],
  };

  return (
    <div style={{ height: 450, marginTop: 20, marginBottom: 20 }}>
       {/* 🟢 NUEVA LEYENDA ESTILO IMAGEN */}
          {consumptionData.length > 0 && <CustomLegend />}
      
            {consumptionData.length ? (
              <DualAxes {...config} style={{ height: 400 }} />
            ) : (
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 400,
                color: "#999",
              }}>
                No hay datos disponibles
              </div>
            )}
    </div>
  );
}

// Componente para mostrar valores intermedios utilizados en los cálculos
function IntermediateValuesDisplay({ resultsProductions, resultsWorkers, t }) {
  const [isOpen, setIsOpen] = useState(false);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Obtener todos los años disponibles y ordenarlos de mayor a menor
  const availableYears = Object.keys(resultsProductions || {}).sort(
    (a, b) => b - a,
  );

  return (
    <div
      style={{
        margin: "20px 0",
        padding: "15px",
        backgroundColor: "#f8f9fa",
        borderRadius: "8px",
        border: "1px solid #dee2e6",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          padding: "10px 0",
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <h4 style={{ margin: 0, color: "#495057" }}>
          <Icon name={isOpen ? "chevron down" : "chevron right"} />
          Valores intermedios para verificación de cálculos - Últimos 5 años
        </h4>
        <span style={{ fontSize: "0.9em", color: "#6c757d" }}>
          {isOpen ? "Haz clic para ocultar" : "Haz clic para ver detalles"}
        </span>
      </div>

      {isOpen && (
        <>
          {availableYears.map((year) => (
            <div key={year} style={{ marginBottom: "30px" }}>
              <h5
                style={{
                  marginTop: "20px",
                  marginBottom: "10px",
                  color: "#495057",
                }}
              >
                Año {year}
              </h5>
              <Table celled size="small" style={{ fontSize: "0.9em" }}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell textAlign="center">Mes</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">
                      Producción (VPS/VFS)
                    </Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">
                      Trabajadores
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {months.map((month) => {
                    const production = resultsProductions?.[year]?.[month] || 0;
                    const workers = resultsWorkers?.[year]?.[month] || 0;

                    return (
                      <Table.Row key={month}>
                        <Table.Cell textAlign="center">
                          {t ? t(month) : month}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {production > 0 ? production.toFixed(2) : "-"}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {workers > 0 ? workers : "-"}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                  <Table.Row
                    style={{ fontWeight: "bold", backgroundColor: "#e9ecef" }}
                  >
                    <Table.Cell textAlign="center">Total</Table.Cell>
                    <Table.Cell textAlign="center">
                      {Object.values(resultsProductions?.[year] || {})
                        .reduce((a, b) => a + b, 0)
                        .toFixed(2)}
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      {Object.values(resultsWorkers?.[year] || {}).reduce(
                        (a, b) => a + b,
                        0,
                      )}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row
                    style={{
                      fontWeight: "bold",
                      backgroundColor: "#d1ecf1",
                      color: "#0c5460",
                    }}
                  >
                    <Table.Cell textAlign="center">Promedio</Table.Cell>
                    <Table.Cell textAlign="center">
                      {(() => {
                        const values = Object.values(
                          resultsProductions?.[year] || {},
                        );
                        const count = values.filter((v) => v > 0).length;
                        const avg =
                          count > 0
                            ? values.reduce((a, b) => a + b, 0) / count
                            : 0;
                        return avg.toFixed(2);
                      })()}
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      {(() => {
                        const values = Object.values(
                          resultsWorkers?.[year] || {},
                        );
                        const count = values.filter((v) => v > 0).length;
                        const avg =
                          count > 0
                            ? values.reduce((a, b) => a + b, 0) / count
                            : 0;
                        return avg.toFixed(0);
                      })()}
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </div>
          ))}

          <div
            style={{
              fontSize: "0.85em",
              color: "#6c757d",
              marginTop: "10px",
              textAlign: "center",
            }}
          >
            <strong>Nota:</strong> Estos valores se utilizan como denominadores
            en los cálculos de intensidad. Producción (VPS/VFS) para intensidad
            industrial y Trabajadores para intensidad doméstica.
          </div>
        </>
      )}
    </div>
  );
}
